/**
 * The Loading vs Capacity view model — the served payload turned into the four quantities
 * the redesigned surface is drawn from, and nothing else.
 *
 * A week on this surface is no longer one load percentage. It is a stack of capacity spent
 * CLEARING backlog / work DUE to start this week / FREE capacity, with STILL QUEUED hours
 * drawn above the capacity line. That decomposition is served — services/loading_level.py
 * walks it against the governed basis — and is deliberately NOT re-derived here: deriving
 * it client-side would fork the definition, which is gap G25 all over again.
 *
 * What this module does is arithmetic the payload leaves to the caller and nothing more:
 * `free` (capacity minus what the week spends), the first week's OWN demand (its bucket
 * holds the folded pre-window backlog too — `overdue_hours` is what takes them apart), and
 * the labels. Both readings of the same hours ride along together: `scheduled`/`loadPct`
 * are the as-scheduled view, the rest is the levelled one.
 */

import type { LoadRow, LoadVsCapacityView } from '../types/loading'
import type { Site } from '../types/metrics'

/** One resource group × week, in both readings. */
export interface WeekModel {
  index: number
  weekStart: string // ISO Monday
  capacity: number
  demand: number // this week's OWN demand — week 1 excludes the folded backlog
  scheduled: number // as-scheduled hours in the bucket (week 1 INCLUDES the backlog)
  loadPct: number | null
  own: number // levelled: this week's own demand that got away
  clearing: number // levelled: capacity spent on work that was already late
  clearingAge: number | null // levelled: how late that work is, in weeks (null = cleared none)
  free: number // levelled: unsold hours
  carry: number // levelled: still queued when the week ends
}

/** One resource group across the window. */
export interface ResourceModel {
  id: string
  entity: string
  site: Site
  /** The denominator the SERVER divided this row by, carried so the week drill asks for the
   *  same one. A drill on a different basis than the cell it opened would not reconcile. */
  basis: string
  name: string
  sub: string // '×6 · 2 pooled' | the lone machine's name
  capacity: number // the standard week the chart scales against
  overdue: number
  weeks: WeekModel[]
  clearIdx: number // -1 = never clears inside the window
  spare: number // Σ free — the sellable hours
  queuedAtEnd: number
  peakPct: number | null
  totalScheduled: number
  /** Hours this row counts but has no denominator for — a group mixing a machine with a
   *  bench. Its percentage is overstated by this share. 0 on every clean group. */
  unmeasured: number
  /** How late this group's OLDEST arrears are, in weeks. null = nothing is late.
   *  ⚠ A minimum over the vintages — see meanArrears, which is what the lane shows. */
  oldestArrears: number | null
  /** How late this group's arrears are on average, weighted by hours. null = nothing late. */
  meanArrears: number | null
}

/** A resource with no agreed capacity basis: hours only, never a ratio (spec §10d). */
export interface ProcessGroup {
  name: string
  hours: number
  site: Site
}

/**
 * The row's STANDARD week — what the lane is labelled "310h/wk" with, and what the chart
 * scales against.
 *
 * ⚠ IT WAS THE FIRST WEEK'S CAPACITY (corrected 20 Aug 2026), which is not a weekly rate.
 * Capacity is resolved per week since 6 Aug: a week holding a bank holiday, the Christmas
 * shutdown or a machine's 2-day calibration genuinely has less of it. In the 13 weeks from
 * 17 Aug 2026 the Summer bank holiday takes STAR from 310h to 250h and two calibration blocks
 * take MILL5AX to 340h — so a window that opened on either would have printed the reduced week
 * as the standard one, and a reader would ask where sixty hours a week had gone. The Christmas
 * shutdown makes that far worse than sixty hours.
 *
 * The server now sends the un-exceptioned figure. The fallback is the old behaviour, so a
 * backend serving the previous payload still renders.
 */
const capacityOf = (row: LoadRow): number =>
  row.capacity_standard_week ?? row.cells.find((c) => c.capacity_hrs != null)?.capacity_hrs ?? 0

/** '×6 · 2 pooled' for a group, the machine's own name when the group is one machine —
 *  a bare '×1' tells a reader nothing they could act on. */
function subtitle(row: LoadRow): string {
  if (row.machine_count === 1 && row.resources.length === 1) return row.resources[0].resource_name
  return `×${row.machine_count}${row.pooled_count > 1 ? ` · ${row.pooled_count} pooled` : ''}`
}

function weeksOf(row: LoadRow, overdue: number): WeekModel[] {
  return row.cells.map((c, i) => {
    const capacity = c.capacity_hrs ?? 0
    const own = c.own_hours ?? 0
    const clearing = c.clearing_hours ?? 0
    return {
      index: i,
      weekStart: c.week_start,
      capacity,
      demand: Math.max(0, c.load_hours - (i === 0 ? overdue : 0)),
      scheduled: c.load_hours,
      loadPct: c.load_pct,
      own,
      clearing,
      clearingAge: c.clearing_age_weeks ?? null,
      free: Math.max(0, capacity - own - clearing),
      carry: c.carry_out_hours ?? 0,
    }
  })
}

function resourceOf(row: LoadRow, entity: string, site: Site, basis: string): ResourceModel {
  const overdue = row.overdue_hours ?? 0
  const weeks = weeksOf(row, overdue)
  return {
    id: `${entity}:${row.resource_group}`,
    entity,
    site,
    basis,
    name: row.resource_group,
    sub: subtitle(row),
    capacity: capacityOf(row),
    overdue,
    weeks,
    clearIdx: row.clears_week ? weeks.findIndex((w) => w.weekStart === row.clears_week) : -1,
    spare: weeks.reduce((a, w) => a + w.free, 0),
    queuedAtEnd: row.carry_out_hours ?? 0,
    peakPct: row.peak_load_pct,
    totalScheduled: row.total_load_hours,
    unmeasured: row.unmeasured_load_hours ?? 0,
    oldestArrears: row.oldest_arrears_weeks ?? null,
    meanArrears: row.arrears_mean_age_weeks ?? null,
  }
}

const SITE_ORDER: Site[] = ['Kirkstall', 'Oracle']

/** The machine rows of one entity's payload, as resources; process rows are split off. */
export function resourcesOf(view: LoadVsCapacityView, site: Site): ResourceModel[] {
  return view.rows
    .filter((r) => r.band === 'machine')
    .map((r) => resourceOf(r, view.entity, site, view.basis))
}

/** Kirkstall's groups, then Oracle's, each ordered by scheduled hours (19 Aug 2026, Mike).
 *  A resource group belongs to ONE entity — resourcesOf stamps each row with the site of the
 *  payload it came from, and no group name is served by both — so a straight sort by hours
 *  interleaved two estates that share no machines, and reading down the lanes meant changing
 *  site every row or two for no reason the page could name. */
export const byEntityThenLoad = (a: ResourceModel, b: ResourceModel): number =>
  SITE_ORDER.indexOf(a.site) - SITE_ORDER.indexOf(b.site) || b.totalScheduled - a.totalScheduled

export function processOf(view: LoadVsCapacityView, site: Site): ProcessGroup[] {
  return view.rows
    .filter((r) => r.band !== 'machine')
    .map((r) => ({ name: r.resource_group, hours: r.total_load_hours, site }))
    .sort((a, b) => b.hours - a.hours)
}

/** The five headline numbers, over the resources in scope. */
export interface LoadingKpis {
  overdue: number
  free: number
  queued: number
  never: ResourceModel[]
  total: number
}

export function kpisOf(resources: ResourceModel[]): LoadingKpis {
  return {
    overdue: resources.reduce((a, r) => a + r.overdue, 0),
    free: resources.reduce((a, r) => a + r.spare, 0),
    queued: resources.reduce((a, r) => a + r.queuedAtEnd, 0),
    never: resources.filter((r) => r.clearIdx === -1),
    total: resources.length,
  }
}
